import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import useFetch from 'use-http';
import { Loader2 } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

export default function ServiceQueueUserInformationDialog({
  open,
  onOpenChange,
  onAccept,
  isLoggedIn,
  initialPhoneNumber = '',
  initialEmail = '',
  initialName = '',
  serviceId,
  workplaceId,
}) {
  const [name, setName] = React.useState(initialName);
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber);
  const [email, setEmail] = React.useState(initialEmail);
  const [errors, setErrors] = React.useState({
    name: '',
    phoneNumber: '',
    email: '',
    submit: '',
  });
  const [loading, setLoading] = React.useState(false);

  const { post: registerToQueue } = useFetch(`/Registrations/RegisterToQueue`);

  React.useEffect(() => {
    if (open) {
      setName(initialName || '');
      setPhoneNumber(initialPhoneNumber || '');
      setEmail(initialEmail || '');
      setErrors({
        name: '',
        phoneNumber: '',
        email: '',
        submit: '',
      });
    }
  }, [open, initialName, initialPhoneNumber, initialEmail]);

  const validate = () => {
    if (isLoggedIn) {
      return true;
    }

    const nextErrors = {
      name: '',
      phoneNumber: '',
      email: '',
      submit: '',
    };

    const trimmedName = name.trim();
    const trimmedPhone = phoneNumber.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      nextErrors.name = 'Vardas yra privalomas.';
    }

    if (!trimmedPhone) {
      nextErrors.phoneNumber = 'Telefono numeris yra privalomas.';
    }

    if (!trimmedEmail) {
      nextErrors.email = 'El. paštas yra privalomas.';
    } else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      nextErrors.email = 'Neteisingas el. pašto formatas.';
    }

    setErrors(nextErrors);

    return !nextErrors.name && !nextErrors.phoneNumber && !nextErrors.email;
  };

  const handleConfirm = async () => {
    if (!validate()) {
      return;
    }

    setLoading(true);

    const request = {
      name: name.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim(),
      serviceId,
      workplaceId,
    };

    const response = await registerToQueue(request);

    setLoading(false);

    if (!response) {
      toast.error('Nepavyko išsiųsti užklausos');
      setErrors((prev) => ({
        ...prev,
        submit: 'Nepavyko užregistruoti į eilę.',
      }));
      return;
    }

    toast.success('Dėkojame, artimiausiu metu su Jumis susisieksime.');
    onOpenChange(false);
    onAccept(response);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
      }}
    >
      <Toaster />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Siųsti registracijos į eilę užklausą</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-6 max-h-[75dvh] overflow-y-auto">
          {isLoggedIn ? (
            <div className="space-y-4 text-center">
              Siųsdami užklausą patvirtinate, kad Jūsų nurodytais kontaktiniais
              duomenimis gali susisiekti Affidea klientų aptarnavimo centro
              komanda.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-2">
                Siųsdami užklausą patvirtinate, kad Jūsų nurodytais
                kontaktiniais duomenimis gali susisiekti Affidea klientų
                aptarnavimo centro komanda.
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-name">Vardas</Label>
                <Input
                  id="service-queue-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Vardas Pavardė"
                />
                {errors.name ? (
                  <p className="text-sm text-destructive">{errors.name}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-phone">Telefono numeris</Label>
                <Input
                  id="service-queue-phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+37061234567"
                />
                {errors.phoneNumber ? (
                  <p className="text-sm text-destructive">
                    {errors.phoneNumber}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-email">El. paštas</Label>
                <Input
                  id="service-queue-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vardas@pastas.lt"
                />
                {errors.email ? (
                  <p className="text-sm text-destructive">{errors.email}</p>
                ) : null}
              </div>

              {errors.submit ? (
                <p className="text-sm text-destructive">{errors.submit}</p>
              ) : null}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Grįžti
          </Button>

          <Button
            variant="secondary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            Siųsti užklausą
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
